import * as Papa from 'papaparse';
import {
  ImportableItem,
  ImportedItemParser,
  ImportedItemRow,
  ImportedItemValidator,
  ImportFormatDataType,
  ImportFormatItem,
} from './importTypes';

/**
 * Extract plain text from structured data according to the provided format.
 * Each item will be on a separate line, and each column will be separated by the specified separator.
 * @param data structured data to be converted to plain text
 * @param format format that defines how to extract data from each row
 * @param separator Tabulator by default or any other separator to be used between columns
 */
export const getPlainTextFromStructuredData = <T>(
  data: readonly ImportedItemRow<T>[],
  format: readonly ImportFormatItem<T>[],
  separator = '\t',
): string => {
  const translated = data.map(row => {
    const rowData = row.inputData;
    const result: string[] = [];

    format.forEach(format => {
      const value = rowData[format.id];
      if (value === undefined) {
        result.push('');
        return;
      }

      const textValue = String(value);
      if (format.dataType === ImportFormatDataType.STRING && textValue.includes(separator)) {
        result.push(`"${textValue}"`);
      } else {
        result.push(textValue);
      }
    });

    return result.join(separator);
  });
  return translated.join('\n');
};

/**
 * Convert a row of plain text cells into a structured object according to the provided format.
 * @param cells array of strings representing the cells in a row
 * @param inputFormat format that defines how to construct the object from the row of strings
 */
/* eslint-disable @typescript-eslint/consistent-type-assertions -- we want to be as much generic as possible */
export const getStructuredDataFromPlainText = <T extends object>(
  cells: string[],
  inputFormat: readonly ImportFormatItem<T>[],
): T => {
  const obj = {} as T;

  inputFormat.forEach((format, index) => {
    const rawValue = cells[index];

    let value: unknown;
    switch (format.dataType) {
      case ImportFormatDataType.NUMBER:
        if (rawValue === '') {
          value = undefined;
        } else {
          const num = Number(rawValue);
          value = Number.isNaN(num) ? undefined : num;
        }
        break;
      case ImportFormatDataType.DATE:
        value = rawValue ? new Date(rawValue) : undefined;
        break;
      case ImportFormatDataType.BOOLEAN:
        value = rawValue === 'true' || rawValue === '1';
        break;
      case ImportFormatDataType.STRING:
      default:
        value = rawValue;
    }

    obj[format.id] = value as T[typeof format.id];
  });

  return obj;
};

export const selectImportableItemParser = <T extends ImportableItem>(
  dataTypes: readonly string[],
): ImportedItemParser<T> | null => {
  return selectParser<T>(dataTypes, getImportableItemParsers<T>());
};

/**
 * Returns only valid rows from the provided list of rows
 * @param rows
 * @returns rows not marked as error
 */
export const getValidRows = <T>(rows: readonly ImportedItemRow<T>[]): ImportedItemRow<T>[] => {
  return rows.filter(row => !row.isError);
};

/**
 * Parsers available for different content types.
 * text/html can be copied from Excel or a web page
 * text/plain can be typed directly or copied from a third party system's export
 */
const getImportableItemParsers = <T extends ImportableItem>(): ImportedItemParser<T>[] => {
  return [
    {
      contentType: 'text/plain',
      parse: parseTabularDataFromPlainText,
    },
    {
      contentType: 'text/html',
      parse: parseTabularDataFromHtml,
    },
  ];
};

const selectParser = <T extends ImportableItem>(
  dataTypes: readonly string[],
  parsers: ImportedItemParser<T>[],
): ImportedItemParser<T> | null => {
  const suitableParser = parsers.find(parser => dataTypes.includes(parser.contentType));
  if (suitableParser) {
    return suitableParser;
  } else {
    return null;
  }
};

/**
 * Process extracted data from a table or plain text according to the input format and validate each row.
 * @param data data extracted from a table or plain text, represented as an array of string arrays
 * @param inputFormat format that defines how to construct objects from each row of strings
 * @param validators validators to be applied to each row of data
 */
const processExtractedData = <T extends ImportableItem>(
  data: string[][],
  inputFormat: readonly ImportFormatItem<T>[],
  validators: readonly ImportedItemValidator<T>[],
): ImportedItemRow<T>[] => {
  const result: ImportedItemRow<T>[] = [];

  // Reset validators before processing to ensure they are in a clean state
  // This is better than creating a new instance of each validator over and over again
  validators.forEach(validator => {
    if (typeof validator === 'function') return;
    validator.reset();
  });

  data.forEach((row, index) => {
    const rowData: T = getStructuredDataFromPlainText(row, inputFormat);
    const rowNumber = index + 1;
    const itemRow: ImportedItemRow<T> = {
      rowNumber: rowNumber,
      inputData: rowData,
      rawData: row,
    };

    const errors: string[] = [];
    const warnings: string[] = [];

    // A validator sees what the ones before it found: a row already refused takes no part in the
    // rules that compare rows, as on the server. An advisory validator's findings are warnings.
    validators.forEach(validator => {
      const validationResult =
        typeof validator === 'function' ? validator(itemRow) : validator.validate(itemRow);
      if (!validationResult || validationResult.length === 0) return;
      if (typeof validator !== 'function' && validator.advisory) {
        warnings.push(...validationResult);
        itemRow.warnings = warnings;
      } else {
        errors.push(...validationResult);
        itemRow.isError = true;
        itemRow.errors = errors;
      }
    });

    result.push(itemRow);
  });

  return result;
};

/**
 * Parse HTML table in the content string. The table should have three columns with warehouse identification (string), product
 * identification (string) and quantity (number)
 * @param content  HTML containing a table to be parsed
 * @param inputFormat
 * @param validators
 * @param parser DOMParser to be used for parsing. If not present, a default DOMParser will be used.
 */
const parseTabularDataFromHtml = <T extends ImportableItem>(
  content: string,
  inputFormat: readonly ImportFormatItem<T>[],
  validators: readonly ImportedItemValidator<T>[],
  parser?: DOMParser,
): ImportedItemRow<T>[] => {
  parser ??= new DOMParser();

  const htmlDoc = parser.parseFromString(content, 'text/html');
  const rows = htmlDoc.querySelectorAll('table tr');

  const extractedRows: string[][] = [];

  rows.forEach(row => {
    const cellNodes = row.querySelectorAll('td');
    const cells: string[] = [];
    cellNodes.forEach(cellNode => cells.push(cellNode.firstChild?.textContent ?? ''));

    extractedRows.push(cells);
  });

  return processExtractedData(extractedRows, inputFormat, validators);
};

/**
 * Parse the content string and return structured data.
 * The string should contain rows of columns separated by either a space, comma, pipe, semicolon or tab.
 * @param content  plain text containing data to be parsed
 * @param inputFormat format of the input data, used to determine how to parse each column
 * @param validators validators to be applied to each row of data
 */
const parseTabularDataFromPlainText = <T extends ImportableItem>(
  content: string,
  inputFormat: readonly ImportFormatItem<T>[],
  validators: readonly ImportedItemValidator<T>[],
): ImportedItemRow<T>[] => {
  const parsedContent = Papa.parse(content, { skipEmptyLines: true });
  const extractedData = parsedContent.data as string[][];

  return processExtractedData(extractedData, inputFormat, validators);
};
